/**
 * Developed by Spell Solutions for Trivinci Systems, LLC.
 *
 * Developer attribution only. Ownership, licensing, and permitted use are
 * governed by the applicable agreement and applicable law.
 */
import { LitElement, css, html } from "lit";
import { customElement, state } from "lit/decorators.js";
import { normalizeLoggerId, setQueryParam } from "../lib/url";
import { getSessions } from "../services/sessions";

/**
 * <rk-setup> — premium logger picker. Fires `connect` event with logger id.
 * Auto-suggests known drivers from the /sessions API as you type.
 */

interface Suggestion {
  loggerId: string;
  driverName?: string;
  status?: string;
}

@customElement("rk-setup")
export class RkSetup extends LitElement {
  static styles = css`
    :host {
      display: grid;
      place-items: center;
      width: 100%;
      height: 100%;
      padding: var(--rk-s-6);
      position: relative;
    }
    .card {
      position: relative;
      width: min(480px, 100%);
      padding: var(--rk-s-8) var(--rk-s-7);
      background: var(--rk-bg-glass);
      backdrop-filter: blur(24px) saturate(140%);
      -webkit-backdrop-filter: blur(24px) saturate(140%);
      border: 1px solid var(--rk-border);
      border-radius: var(--rk-r-xl);
      box-shadow: var(--rk-shadow-lg);
      overflow: hidden;
    }
    .card::before {
      content: "";
      position: absolute;
      inset: 0;
      background: var(--rk-grad-card);
      pointer-events: none;
    }
    .brand {
      display: flex;
      align-items: center;
      gap: 12px;
      margin-bottom: var(--rk-s-6);
    }
    .brand img {
      height: 40px;
      width: auto;
      max-width: 200px;
      object-fit: contain;
      display: block;
      filter: drop-shadow(0 4px 14px rgba(0, 0, 0, 0.5));
    }
    h1 {
      margin: 0 0 var(--rk-s-2);
      font-size: var(--rk-text-2xl);
      letter-spacing: -0.02em;
      font-weight: 800;
    }
    .lede {
      color: var(--rk-text-muted);
      font-size: var(--rk-text-base);
      margin-bottom: var(--rk-s-6);
    }
    .field {
      position: relative;
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 12px 14px;
      background: rgba(255, 255, 255, 0.04);
      border: 1px solid var(--rk-border-strong);
      border-radius: var(--rk-r-md);
      transition: border-color var(--rk-dur-base),
        box-shadow var(--rk-dur-base);
    }
    .field:focus-within {
      border-color: var(--rk-brand-primary);
      box-shadow: 0 0 0 4px rgba(56, 189, 248, 0.16);
    }
    .field svg { color: var(--rk-text-muted); }
    input {
      flex: 1;
      background: none;
      border: none;
      outline: none;
      color: var(--rk-text);
      font-size: var(--rk-text-md);
      font-family: var(--rk-font-mono);
      letter-spacing: 0.04em;
    }
    input::placeholder { color: var(--rk-text-dim); }

    .suggestions {
      margin-top: 8px;
      max-height: 240px;
      overflow-y: auto;
      border-radius: var(--rk-r-md);
      border: 1px solid var(--rk-border);
      background: var(--rk-bg-2);
      opacity: 0;
      transform: translateY(-6px);
      pointer-events: none;
      transition: opacity var(--rk-dur-base) var(--rk-ease-out),
        transform var(--rk-dur-base) var(--rk-ease-out);
    }
    .suggestions[show] {
      opacity: 1;
      transform: none;
      pointer-events: auto;
    }
    .sug {
      display: grid;
      grid-template-columns: 1fr auto;
      gap: 8px;
      align-items: center;
      padding: 10px 12px;
      cursor: pointer;
      border-bottom: 1px solid rgba(255, 255, 255, 0.04);
      transition: background var(--rk-dur-fast);
    }
    .sug:last-child { border-bottom: none; }
    .sug:hover, .sug[selected] {
      background: rgba(56, 189, 248, 0.1);
    }
    .sug-driver {
      font-weight: 600;
      color: var(--rk-text);
    }
    .sug-driver mark {
      background: rgba(56, 189, 248, 0.25);
      color: var(--rk-text);
      padding: 0 2px;
      border-radius: 3px;
    }
    .sug-id {
      font-family: var(--rk-font-mono);
      font-size: 11px;
      color: var(--rk-text-muted);
    }
    .sug-status {
      font-family: var(--rk-font-mono);
      font-size: 10px;
      letter-spacing: 0.14em;
      text-transform: uppercase;
      padding: 2px 8px;
      border-radius: var(--rk-r-pill);
      border: 1px solid var(--rk-border);
      color: var(--rk-text-muted);
    }
    .sug-status[data-status="active"] {
      color: var(--rk-ok);
      border-color: rgba(52, 211, 153, 0.3);
      background: rgba(52, 211, 153, 0.08);
    }

    .actions {
      margin-top: var(--rk-s-5);
      display: flex;
      gap: 10px;
      align-items: center;
    }
    button.connect {
      flex: 1;
      padding: 13px 18px;
      border-radius: var(--rk-r-md);
      font-weight: 700;
      font-size: var(--rk-text-md);
      color: #06121e;
      background: linear-gradient(135deg, var(--rk-brand-primary) 0%, var(--rk-brand-accent) 100%);
      border: none;
      cursor: pointer;
      transition: transform var(--rk-dur-fast) var(--rk-ease-spring),
        box-shadow var(--rk-dur-fast);
      box-shadow: 0 8px 24px rgba(56, 189, 248, 0.35);
    }
    button.connect:hover {
      transform: translateY(-1px);
      box-shadow: 0 12px 32px rgba(56, 189, 248, 0.5);
    }
    button.connect:disabled {
      opacity: 0.45;
      cursor: not-allowed;
      transform: none;
      box-shadow: none;
    }
    a.race {
      font-size: var(--rk-text-sm);
      color: var(--rk-text-muted);
      text-decoration: none;
      padding: 12px 14px;
      border-radius: var(--rk-r-md);
      border: 1px solid var(--rk-border);
      transition: border-color var(--rk-dur-fast),
        background var(--rk-dur-fast);
    }
    a.race:hover {
      border-color: var(--rk-border-strong);
      background: rgba(255, 255, 255, 0.04);
      color: var(--rk-text);
    }
  `;

  @state() private query = "";
  @state() private suggestions: Suggestion[] = [];
  @state() private selectedIdx = -1;
  @state() private loading = false;

  private allSessions: Suggestion[] = [];

  async connectedCallback(): Promise<void> {
    super.connectedCallback();
    this.loading = true;
    try {
      const map = await getSessions();
      this.allSessions = Array.from(map.values()).map((s: any) => ({
        loggerId: String(s.logger_id),
        driverName: s.driver_name,
        status: s.status,
      }));
    } catch {
      /* fall back to manual entry */
    } finally {
      this.loading = false;
    }
  }

  private onInput = (ev: Event) => {
    const v = (ev.target as HTMLInputElement).value;
    this.query = v;
    this.selectedIdx = -1;
    this.recompute();
  };

  private recompute(): void {
    const q = this.query.trim().toLowerCase();
    if (!q) {
      this.suggestions = [];
      return;
    }
    const matches = this.allSessions.filter((s) => {
      const id = s.loggerId.toLowerCase();
      const name = (s.driverName ?? "").toLowerCase();
      return id.includes(q) || name.includes(q);
    });
    matches.sort((a, b) => {
      // Prioritise prefix matches on driverName, then id startsWith.
      const an = (a.driverName ?? "").toLowerCase();
      const bn = (b.driverName ?? "").toLowerCase();
      const ap = an.startsWith(q) ? 0 : a.loggerId.startsWith(q) ? 1 : 2;
      const bp = bn.startsWith(q) ? 0 : b.loggerId.startsWith(q) ? 1 : 2;
      return ap - bp;
    });
    this.suggestions = matches.slice(0, 8);
  }

  private onKey = (ev: KeyboardEvent) => {
    if (ev.key === "ArrowDown") {
      ev.preventDefault();
      this.selectedIdx = Math.min(this.suggestions.length - 1, this.selectedIdx + 1);
    } else if (ev.key === "ArrowUp") {
      ev.preventDefault();
      this.selectedIdx = Math.max(-1, this.selectedIdx - 1);
    } else if (ev.key === "Enter") {
      ev.preventDefault();
      if (this.selectedIdx >= 0 && this.suggestions[this.selectedIdx]) {
        this.connect(this.suggestions[this.selectedIdx].loggerId);
      } else if (this.query.trim()) {
        this.connect(normalizeLoggerId(this.query));
      }
    } else if (ev.key === "Escape") {
      this.suggestions = [];
    }
  };

  private connect(loggerId: string): void {
    const id = normalizeLoggerId(loggerId);
    if (!id) return;
    setQueryParam("loggerId", id);
    this.dispatchEvent(new CustomEvent("connect", { detail: { loggerId: id } }));
  }

  private highlight(text: string): unknown {
    if (!this.query) return text;
    const idx = text.toLowerCase().indexOf(this.query.toLowerCase());
    if (idx < 0) return text;
    const before = text.slice(0, idx);
    const match = text.slice(idx, idx + this.query.length);
    const after = text.slice(idx + this.query.length);
    return html`${before}<mark>${match}</mark>${after}`;
  }

  render() {
    const canConnect = this.query.trim().length > 0;
    return html`
      <div class="card">
        <div class="brand">
          <img src="logo.png" alt="RaceKeeper"/>
        </div>
        <h1>Live Race Player</h1>
        <p class="lede">Pick a car by driver name or logger ID.</p>

        <div class="field">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="11" cy="11" r="7"/><path d="m20 20-3.5-3.5"/>
          </svg>
          <input
            type="text"
            placeholder="Driver name or logger ID…"
            spellcheck="false"
            autocomplete="off"
            .value=${this.query}
            @input=${this.onInput}
            @keydown=${this.onKey}
            autofocus
          />
        </div>

        <div class="suggestions" ?show=${this.suggestions.length > 0}>
          ${this.suggestions.map(
            (s, i) => html`
              <div
                class="sug"
                ?selected=${i === this.selectedIdx}
                @click=${() => this.connect(s.loggerId)}
              >
                <div>
                  <div class="sug-driver">
                    ${this.highlight(s.driverName || `Logger ${s.loggerId.slice(-4)}`)}
                  </div>
                  <div class="sug-id">${this.highlight(s.loggerId)}</div>
                </div>
                <div class="sug-status" data-status=${(s.status ?? "").toLowerCase()}>
                  ${s.status ?? "—"}
                </div>
              </div>
            `
          )}
        </div>

        <div class="actions">
          <button class="connect" ?disabled=${!canConnect} @click=${() => this.connect(this.query)}>
            Watch live
          </button>
          <a class="race" href="./race.html">Multi-view →</a>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "rk-setup": RkSetup;
  }
}
